// src/pages/MapPage.jsx
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  GoogleMap,
  Polyline,
  InfoWindow,
  OverlayView,
  Marker,
  DirectionsRenderer,
  useJsApiLoader,
} from '@react-google-maps/api';
import {
  IoLogOutOutline,
  IoLayersOutline,
  IoNavigateOutline,
  IoEyeOutline,
  IoEyeOffOutline,
  IoTextOutline,
  IoStorefrontOutline,
  IoAddCircleOutline,
  IoCloseCircleOutline,
} from 'react-icons/io5';
import { useNavigate } from 'react-router-dom';
import './FleetMap.css';

// Components
// import VehicleList from './components/VehicleList';
import SalesPointList from './components/SalesPointList';
import RouteNetworkPage from './components/RouteNetworkPage';

// API
import api, { routesAPI, salesPointsAPI, depotsAPI, usersAPI } from './services/api';
import { decodeToken } from './utils/auth';

// Auth guard
const useRequireAuth = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem('access');
      if (!token) {
        navigate('/', { replace: true });
      }
    };
    checkAuth();
    window.addEventListener('popstate', checkAuth);
    return () => window.removeEventListener('popstate', checkAuth);
  }, [navigate]);
};

// Map configuration
const mapContainerStyle = { width: '100%', height: '100%' };
const defaultCenter = { lat: 0.3476, lng: 32.5825 }; // Kampala, Uganda

const ENTITY_FIELD_CANDIDATES = [
  'entityinstance', 'entity_instance', 'entityInstance',
  'reference_entity',
  'organization', 'organization_id',
  'customer', 'customer_id',
  'entity', 'entity_id',
  'depot', 'depot_id',
  'member_of', 'memberships',
];

function collectIdsFromValue(value, ids) {
  if (value === null || value === undefined || value === '') return;

  if (Array.isArray(value)) {
    value.forEach(item => collectIdsFromValue(item, ids));
    return;
  }

  if (typeof value === 'object') {
    if (value.id !== undefined && value.id !== null && value.id !== '') ids.add(String(value.id));
    if (value.uuid !== undefined && value.uuid !== null && value.uuid !== '') ids.add(String(value.uuid));
    ENTITY_FIELD_CANDIDATES.forEach(field => {
      if (value[field] !== undefined && value[field] !== value) {
        collectIdsFromValue(value[field], ids);
      }
    });
    return;
  }

  if (typeof value === 'string' || typeof value === 'number') {
    ids.add(String(value));
  }
}

function collectIdsFromObjectFields(source, fieldNames) {
  const ids = new Set();
  if (!source || typeof source !== 'object') return ids;

  fieldNames.forEach(field => {
    if (source[field] !== undefined) collectIdsFromValue(source[field], ids);
  });

  return ids;
}

function getLoggedInUserEntityIds(...sources) {
  const ids = new Set();

  sources.forEach(source => {
    if (!source || typeof source !== 'object') return;

    collectIdsFromObjectFields(source, ENTITY_FIELD_CANDIDATES).forEach(id => ids.add(id));

    ['profile', 'user_profile', 'userProfile', 'user'].forEach(nestedKey => {
      const nested = source[nestedKey];
      if (!nested || nested === source) return;
      collectIdsFromObjectFields(nested, ENTITY_FIELD_CANDIDATES).forEach(id => ids.add(id));
    });
  });

  return ids;
}

export default function MapPage() {
  useRequireAuth();

  // Google Maps
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_MAPS_API_KEY || '',
    // Key is injected at build time via REACT_APP_MAPS_API_KEY env var
  });

  // State
  // const [vehicleLocationsById, setVehicleLocationsById] = useState({});
  // const [panelVehicles, setPanelVehicles] = useState([]);
  // const [selectedVehicleId, setSelectedVehicleId] = useState(null);
  const [selectedRoutes, setSelectedRoutes] = useState([]);
  const [routeDirections, setRouteDirections] = useState({});
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [loading] = useState(false);
  // const [refreshingVehicles, setRefreshingVehicles] = useState(false);
  const [activeTab, setActiveTab] = useState('map');
  const [showWaypoints, setShowWaypoints] = useState(true);
  // const [showVehicles, setShowVehicles] = useState(true);
  const [showLabels, setShowLabels] = useState(true);
  const [showMarkerDetails, setShowMarkerDetails] = useState(true);
  const [focusedSalesPointId, setFocusedSalesPointId] = useState(null);

  // Entity & Sales Points state
  const [entities, setEntities] = useState([]);
  const [selectedEntityId, setSelectedEntityId] = useState('');
  const [allSalesPoints, setAllSalesPoints] = useState([]);
  const [loadingSalesPoints, setLoadingSalesPoints] = useState(false);
  const [showSalesPoints, setShowSalesPoints] = useState(true);
  const [clickedSalesPoint, setClickedSalesPoint] = useState(null);
  const [addToRouteId, setAddToRouteId] = useState('');
  const [allRoutes, setAllRoutes] = useState([]);
  const [addingToRoute, setAddingToRoute] = useState(false);
  const [mapClickCoord, setMapClickCoord] = useState(null);
  const [coordCopied, setCoordCopied] = useState(false);

  // Map styles to hide all labels
  const noLabelStyles = useMemo(() => showLabels ? [] : [
    {
      featureType: 'all',
      elementType: 'labels',
      stylers: [{ visibility: 'off' }],
    },
  ], [showLabels]);

  // const vehicleLocations = useMemo(
  //   () => Object.values(vehicleLocationsById),
  //   [vehicleLocationsById]
  // );
  // const panelVehiclesById = useMemo(
  //   () => Object.fromEntries(panelVehicles.map((v) => [v.id || v.vehicle_id, v])),
  //   [panelVehicles]
  // );
  // const selectedVehicle = useMemo(() => {
  //   if (!selectedVehicleId) return null;
  //   return vehicleLocationsById[selectedVehicleId] || panelVehiclesById[selectedVehicleId] || null;
  // }, [selectedVehicleId, vehicleLocationsById, panelVehiclesById]);

  // VEHICLE POLLING DISABLED
  // const fetchVehicleLocations = useCallback(async ({ syncSidePanel = false } = {}) => { ... }, []);

  // Vehicle polling disabled
  // useEffect(() => {
  //   fetchVehicleLocations({ syncSidePanel: true });
  //   const interval = setInterval(() => fetchVehicleLocations(), 30000);
  //   return () => clearInterval(interval);
  // }, [fetchVehicleLocations]);

  // Resolve logged-in user's entityinstance first
  useEffect(() => {
    let cancelled = false;

    const resolveUserEntity = async () => {
      try {
        const tokenPayload = decodeToken();
        const [authProfileRes, fullProfileRes] = await Promise.all([
          usersAPI.getProfile().catch(() => null),
          api.get('/api/profile/').catch(() => null),
        ]);

        if (cancelled) return;

        const entityIds = Array.from(getLoggedInUserEntityIds(
          tokenPayload,
          authProfileRes?.data,
          fullProfileRes?.data,
        ));

        if (entityIds.length > 0) {
          setSelectedEntityId(prev => prev || entityIds[0]);
        }
      } catch {
        // Keep UI functional even if profile endpoints fail
      }
    };

    resolveUserEntity();

    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch entities (organizations) on mount
  useEffect(() => {
    const fetchEntities = async () => {
      try {
        const res = await depotsAPI.getAll();
        const data = res.data?.results || res.data || [];
        setEntities(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Failed to fetch entities:', err);
        if (selectedEntityId) {
          setEntities([{ id: selectedEntityId, name: 'My Entity' }]);
        }
      }
    };
    fetchEntities();
  }, [selectedEntityId]);

  // Fetch all routes for the add-to-route dropdown
  useEffect(() => {
    const fetchAllRoutes = async () => {
      try {
        const res = await routesAPI.getAll();
        let data = [];
        if (Array.isArray(res.data)) data = res.data;
        else if (res.data?.results) data = res.data.results;
        setAllRoutes(data);
      } catch (err) {
        console.error('Failed to fetch routes list:', err);
      }
    };
    fetchAllRoutes();
  }, []);

  // Fetch mapped sales points (prefer user-entity scoped query)
  useEffect(() => {
    const fetchAllSalesPoints = async () => {
      try {
        setLoadingSalesPoints(true);
        
        // Fetch all pages of sales points
        let spData = [];
        let nextUrl = null;

        const paramCandidates = selectedEntityId
          ? [
              { customer_id: selectedEntityId, is_mapped: true },
              { customer: selectedEntityId, is_mapped: true },
              { entity_id: selectedEntityId, is_mapped: true },
              { entity_instance: selectedEntityId, is_mapped: true },
              { is_mapped: true },
            ]
          : [{ is_mapped: true }];

        let firstRes = null;
        for (const params of paramCandidates) {
          try {
            firstRes = await salesPointsAPI.getAll(params);
            break;
          } catch (e) {
            if (e?.response?.status !== 500) throw e;
          }
        }

        if (!firstRes) throw new Error('Unable to load sales points');
        
        if (Array.isArray(firstRes.data)) {
          spData = firstRes.data;
        } else if (firstRes.data?.results) {
          spData = firstRes.data.results;
          nextUrl = firstRes.data.next;
        } else if (firstRes.data?.features) {
          spData = firstRes.data.features.map(f => ({ ...f.properties, lat: f.geometry?.coordinates?.[1], lng: f.geometry?.coordinates?.[0] }));
        }

        // Fetch remaining pages if paginated
        while (nextUrl) {
          try {
            const pageRes = await api.get(nextUrl);
            if (pageRes.data?.results) {
              spData = [...spData, ...pageRes.data.results];
              nextUrl = pageRes.data.next;
            } else {
              nextUrl = null;
            }
          } catch {
            nextUrl = null;
          }
        }

        // Only keep mapped sales points with valid coordinates
        // Handle is_mapped as boolean true, string "True"/"true", or number 1
        const mapped = spData.filter(sp => {
          const isMapped = sp.is_mapped === true || sp.is_mapped === 'True' || sp.is_mapped === 'true' || sp.is_mapped === 1 || sp.is_mapped === '1';
          if (!isMapped) return false;
          const lat = parseFloat(sp.lat || sp.latitude);
          const lng = parseFloat(sp.lng || sp.longitude);
          return !isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0;
        });

        setAllSalesPoints(mapped);
      } catch (err) {
        console.error('Failed to fetch sales points:', err);
        setAllSalesPoints([]);
      } finally {
        setLoadingSalesPoints(false);
      }
    };
    fetchAllSalesPoints();
  }, [selectedEntityId]);

  // Filter sales points by selected entity (client-side)
  // If routes are also selected, only show sales points that are on those routes
  const entitySalesPoints = useMemo(() => {
    if (!selectedEntityId || allSalesPoints.length === 0) return [];

    const filtered = allSalesPoints.filter(sp => {
      // reference_entity is an array of objects, e.g. [{ id: "uuid", name: "Urban World" }]
      if (Array.isArray(sp.reference_entity)) {
        return sp.reference_entity.some(
          e => e.id === selectedEntityId || e === selectedEntityId
        );
      }
      // Fallback: direct field match
      const spEntity = sp.reference_entity || sp.entity_instance || sp.entity_id
        || sp.entity || sp.customer || sp.customer_id;
      return spEntity === selectedEntityId;
    });

    // If routes are selected, further filter to only sales points on those routes
    if (selectedRoutes.length > 0) {
      const routeSalesPointIds = new Set();
      selectedRoutes.forEach(route => {
        (route.waypoints || []).forEach(wp => {
          if (wp.salespoint?.id) {
            routeSalesPointIds.add(wp.salespoint.id);
          }
        });
      });
      const onRoute = filtered.filter(sp => routeSalesPointIds.has(sp.id));
      console.log(`Filtered ${onRoute.length} sales points on selected routes (from ${filtered.length} for entity)`);
      return onRoute;
    }

    console.log(`Filtered ${filtered.length} mapped sales points for entity ${selectedEntityId}`);
    return filtered;
  }, [selectedEntityId, allSalesPoints, selectedRoutes]);

  // Center map when entity selection changes and has sales points
  useEffect(() => {
    if (entitySalesPoints.length > 0) {
      const first = entitySalesPoints[0];
      const lat = parseFloat(first.lat || first.latitude);
      const lng = parseFloat(first.lng || first.longitude);
      if (!isNaN(lat) && !isNaN(lng)) {
        setMapCenter({ lat, lng });
      }
    }
  }, [entitySalesPoints]);

  // Handle adding a sales point to a route
  const handleAddSalesPointToRoute = useCallback(async (salesPoint, routeId) => {
    if (!salesPoint || !routeId) return;
    try {
      setAddingToRoute(true);
      await routesAPI.addWaypoint(routeId, {
        salespoint_id: salesPoint.id,
        is_required: true,
        notify_on_approach: true,
      });
      alert(`${salesPoint.name} added to route successfully!`);
      setClickedSalesPoint(null);
      setAddToRouteId('');
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.detail || 'Failed to add to route';
      alert(msg);
      console.error('Failed to add sales point to route:', err);
    } finally {
      setAddingToRoute(false);
    }
  }, []);

  // Handle salespoint selection from the sidebar list
  const handleSalesPointListSelect = useCallback((sp) => {
    if (focusedSalesPointId === sp.id) {
      // Deselect - show all salespoints again
      setFocusedSalesPointId(null);
      setClickedSalesPoint(null);
    } else {
      setFocusedSalesPointId(sp.id);
      setClickedSalesPoint(sp);
      const lat = parseFloat(sp.lat || sp.latitude);
      const lng = parseFloat(sp.lng || sp.longitude);
      if (!isNaN(lat) && !isNaN(lng)) {
        setMapCenter({ lat, lng });
      }
    }
  }, [focusedSalesPointId]);


  // Extract waypoints from all selected routes
  const allRouteWaypoints = useMemo(() => {
    if (selectedRoutes.length === 0) {
      return [];
    }

    return selectedRoutes.flatMap(route => {
      if (!route?.waypoints) {
        return [];
      }

      return route.waypoints
        .filter(wp => {
          const hasSalespoint = wp.salespoint;
          const hasLocation = wp.salespoint?.lat && wp.salespoint?.lng;
          if (!hasLocation) {
            console.log('Waypoint missing location:', wp);
          }
          return hasSalespoint && hasLocation;
        })
        .map(wp => ({
          id: wp.id,
          routeId: route.id,
          routeColor: route.color_hex || '#2196F3',
          name: wp.salespoint?.name || 'Unknown',
          order: wp.sequence_order,
          lat: wp.salespoint.lat,
          lng: wp.salespoint.lng,
        }))
        .sort((a, b) => a.order - b.order);
    });
  }, [selectedRoutes]);

  const routeWaypoints = useMemo(() => {
    if (selectedRoutes.length === 0) {
      console.log('No selected routes');
      return [];
    }
    
    return allRouteWaypoints;
  }, [allRouteWaypoints]);

  // Logout
  const handleLogout = () => {
    localStorage.removeItem('access');
    window.location.href = '/';
  };

  if (!isLoaded) {
    return (
      <div className="fleet-app">
        <div className="loading-overlay">
          <div className="loading-spinner" />
        </div>
      </div>
    );
  }

  return (
    <div className="fleet-app">
      {/* Top Navigation Bar */}
      <div className="top-bar">
        <div className="top-bar-left">
          <h1>PALLI Routes</h1>
          <div className="nav-tabs">
            <button
              className={`nav-tab ${activeTab === 'map' ? 'active' : ''}`}
              onClick={() => setActiveTab('map')}
            >
              <IoLayersOutline size={16} style={{ marginRight: 6 }} />
              Map View
            </button>
            <button
              className={`nav-tab ${activeTab === 'routes' ? 'active' : ''}`}
              onClick={() => setActiveTab('routes')}
            >
              <IoNavigateOutline size={16} style={{ marginRight: 6 }} />
              Routes
            </button>
          </div>
        </div>
        <div className="top-bar-right">
          <button className="logout-btn" onClick={handleLogout}>
            <IoLogOutOutline size={16} />
            Logout
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="main-content">
        {activeTab === 'routes' ? (
          <RouteNetworkPage />
        ) : (
        <>
        {/* Left Sidebar - Sales Points */}
        <div className="sidebar-left">
          <div className="panel-half">
            <SalesPointList
              salesPoints={entitySalesPoints}
              selectedId={focusedSalesPointId}
              onSelect={handleSalesPointListSelect}
              entityName={entities.find(e => e.id === selectedEntityId)?.name || ''}
            />
          </div>
        </div>

        {/* Map Container */}
        <div className="map-wrapper">
          {/* Map Toolbar */}
          <div className="map-toolbar">
            <div className="map-toolbar-left">
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div className="entity-selector">
                  <IoStorefrontOutline size={16} style={{ color: '#666', flexShrink: 0 }} />
                  <select
                    value={selectedEntityId}
                    onChange={(e) => {
                      setSelectedEntityId(e.target.value);
                      setClickedSalesPoint(null);
                    }}
                    className="entity-dropdown"
                  >
                    <option value="">All Entities</option>
                    {entities.map(entity => (
                      <option key={entity.id} value={entity.id}>
                        {entity.name || entity.entity_name || entity.id}
                      </option>
                    ))}
                  </select>
                  {loadingSalesPoints && (
                    <span style={{ fontSize: 11, color: '#999' }}>Loading...</span>
                  )}
                  {selectedEntityId && !loadingSalesPoints && entitySalesPoints.length > 0 && (
                    <span className="sp-badge">{entitySalesPoints.length} pts</span>
                  )}
                  {selectedEntityId && !loadingSalesPoints && entitySalesPoints.length === 0 && (
                    <span style={{ fontSize: 11, color: '#999' }}>0 mapped pts</span>
                  )}
                </div>
                <span style={{ fontSize: 13, color: '#999' }}>|</span>
                <span style={{ fontSize: 14, color: '#666', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {selectedRoutes.length > 0 ? (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        {selectedRoutes.slice(0, 3).map(route => (
                          <div key={route.id} style={{
                            width: '16px',
                            height: '16px',
                            backgroundColor: route.color_hex || '#2196F3',
                            borderRadius: '4px',
                            border: '1px solid #fff',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
                          }} />
                        ))}
                        {selectedRoutes.length > 3 && (
                          <span style={{ fontSize: '12px', color: '#999' }}>+{selectedRoutes.length - 3}</span>
                        )}
                      </div>
                      <strong>Routes:</strong> {selectedRoutes.length} selected
                    </>
                  ) : (
                    'Select routes to view on map'
                  )}
                </span>
              </div>
            </div>
            <div className="map-toolbar-right">
              <button
                className={`toolbar-btn ${!showLabels ? 'active' : ''}`}
                onClick={() => setShowLabels(!showLabels)}
                title={showLabels ? 'Hide map labels' : 'Show map labels'}
              >
                <IoTextOutline size={16} />
                {showLabels ? 'Hide Labels' : 'Show Labels'}
              </button>
              <button
                className={`toolbar-btn ${!showMarkerDetails ? 'active' : ''}`}
                onClick={() => setShowMarkerDetails(!showMarkerDetails)}
                title={showMarkerDetails ? 'Hide marker details' : 'Show marker details'}
              >
                {showMarkerDetails ? <IoEyeOffOutline size={16} /> : <IoEyeOutline size={16} />}
                {showMarkerDetails ? 'Hide Details' : 'Show Details'}
              </button>
              {selectedEntityId && entitySalesPoints.length > 0 && (
                <button
                  className={`toolbar-btn ${showSalesPoints ? 'active' : ''}`}
                  onClick={() => setShowSalesPoints(!showSalesPoints)}
                  title={showSalesPoints ? 'Hide sales points' : 'Show sales points'}
                >
                  <IoStorefrontOutline size={16} />
                  {showSalesPoints ? 'Hide' : 'Show'} Sales Pts
                </button>
              )}
              {selectedRoutes.length > 0 && (
                <>
                  <button
                    className={`toolbar-btn ${showWaypoints ? 'active' : ''}`}
                    onClick={() => setShowWaypoints(!showWaypoints)}
                    title={showWaypoints ? 'Hide waypoints' : 'Show waypoints'}
                  >
                    {showWaypoints ? <IoEyeOutline size={16} /> : <IoEyeOffOutline size={16} />}
                    {showWaypoints ? 'Hide' : 'Show'} Waypoints
                  </button>
                  <button
                    className="toolbar-btn"
                    onClick={() => {
                      setSelectedRoutes([]);
                      setRouteDirections({});
                    }}
                  >
                    Clear Routes ({selectedRoutes.length})
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Map */}
          <div className="map-container">
            {loading && (
              <div className="loading-overlay">
                <div className="loading-spinner" />
              </div>
            )}
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              center={mapCenter}
              zoom={12}
              options={{
                streetViewControl: false,
                mapTypeControl: true,
                fullscreenControl: true,
                styles: noLabelStyles,
              }}
              onClick={(e) => {
                setMapClickCoord({ lat: e.latLng.lat(), lng: e.latLng.lng() });
                setCoordCopied(false);
              }}
            >
              {/* Vehicle Markers - DISABLED */}

              {/* Route Directions - Road-following paths (chunked for large routes) */}
              {selectedRoutes.map((route) => {
                const directionsArray = routeDirections[route.id];
                if (!directionsArray || !Array.isArray(directionsArray)) return null;
                
                return directionsArray.map((directions, idx) => (
                  <DirectionsRenderer
                    key={`${route.id}-chunk-${idx}`}
                    directions={directions}
                    options={{
                      suppressMarkers: true,
                      polylineOptions: {
                        strokeColor: route.color_hex || '#2196F3',
                        strokeWeight: 6,
                        strokeOpacity: 0.9,
                      },
                    }}
                  />
                ));
              })}

              {/* Route Waypoints - Colored Circle Markers */}
              {showWaypoints && routeWaypoints.map((wp) => (
                <OverlayView
                  key={`${wp.routeId}-${wp.id}`}
                  position={{ lat: wp.lat, lng: wp.lng }}
                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                >
                  <div
                    className="waypoint-marker"
                    title={`${wp.order}. ${wp.name}`}
                  >
                    <div className="circle" style={{ backgroundColor: wp.routeColor || '#D16A2D' }} />
                    <span className="label">
                      {wp.order}
                    </span>
                  </div>
                </OverlayView>
              ))}

              {/* Entity Sales Points Markers */}
              {showSalesPoints && selectedEntityId && entitySalesPoints
                .filter(sp => !focusedSalesPointId || sp.id === focusedSalesPointId)
                .map((sp) => {
                  const lat = parseFloat(sp.lat || sp.latitude);
                  const lng = parseFloat(sp.lng || sp.longitude);
                  if (isNaN(lat) || isNaN(lng)) return null;

                  return (
                    <OverlayView
                      key={`sp-${sp.id}`}
                      position={{ lat, lng }}
                      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                    >
                      <div
                        className="salespoint-marker"
                        onClick={() => setClickedSalesPoint(sp)}
                        title={sp.name}
                      >
                        <div className="diamond" />
                        {showMarkerDetails && <span className="label">{sp.name}</span>}
                      </div>
                    </OverlayView>
                  );
                })}

              {/* Sales Point InfoWindow - Add to Route */}
              {showMarkerDetails && clickedSalesPoint && (() => {
                const lat = parseFloat(clickedSalesPoint.lat || clickedSalesPoint.latitude);
                const lng = parseFloat(clickedSalesPoint.lng || clickedSalesPoint.longitude);
                const assignedSalespeople = Array.isArray(clickedSalesPoint.assigned_salesperson)
                  ? clickedSalesPoint.assigned_salesperson
                  : [];
                const assignedSalespeopleText = assignedSalespeople
                  .map((sp) => sp?.full_name || sp?.name || sp?.username || sp?.email || null)
                  .filter(Boolean)
                  .join(', ');
                if (isNaN(lat) || isNaN(lng)) return null;
                return (
                  <InfoWindow
                    position={{ lat, lng }}
                    onCloseClick={() => { setClickedSalesPoint(null); setAddToRouteId(''); }}
                  >
                    <div className="sp-popup">
                      <h4>{clickedSalesPoint.name}</h4>
                      {clickedSalesPoint.phone_num && (
                        <p style={{ margin: '4px 0', fontSize: 13, color: '#555' }}>
                          📞 {clickedSalesPoint.phone_num}
                          
                        </p>
                      )}
                      <p style={{ margin: '4px 0', fontSize: 12, color: '#888' }}>
                        Status: <strong style={{ color: clickedSalesPoint.status === 'Confirmed' ? '#1F8B50' : '#D16A2D' }}>{clickedSalesPoint.status || 'N/A'}</strong>
                      </p>
                      <p style={{ margin: '4px 0', fontSize: 12, color: '#666' }}>
                        Salesperson: <strong>{assignedSalespeopleText || 'Not assigned'}</strong>
                      </p>
                      <p style={{ margin: '4px 0', fontSize: 12, color: '#666' }}>
                        📍 <strong style={{ fontFamily: 'monospace' }}>{lat.toFixed(6)}, {lng.toFixed(6)}</strong>
                      </p>
                      <div className="sp-add-route">
                        <label style={{ fontSize: 12, fontWeight: 600, color: '#333', marginBottom: 4, display: 'block' }}>
                          <IoAddCircleOutline size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                          Add to Route
                        </label>
                        <select
                          value={addToRouteId}
                          onChange={(e) => setAddToRouteId(e.target.value)}
                          className="sp-route-select"
                        >
                          <option value="">Select a route...</option>
                          {allRoutes.map(r => (
                            <option key={r.id} value={r.id}>{r.name} ({r.code})</option>
                          ))}
                        </select>
                        <button
                          className="sp-add-btn"
                          disabled={!addToRouteId || addingToRoute}
                          onClick={() => handleAddSalesPointToRoute(clickedSalesPoint, addToRouteId)}
                        >
                          {addingToRoute ? 'Adding...' : 'Add to Route'}
                        </button>
                      </div>
                    </div>
                  </InfoWindow>
                );
              })()}
            </GoogleMap>

            {/* Coordinate Picker Overlay */}
            {mapClickCoord && (
              <div className="coord-picker">
                <span className="coord-label">📍 Clicked Coordinates</span>
                <span className="coord-value">
                  {mapClickCoord.lat.toFixed(6)}, {mapClickCoord.lng.toFixed(6)}
                </span>
                <button
                  className="coord-copy-btn"
                  onClick={() => {
                    navigator.clipboard.writeText(`${mapClickCoord.lat.toFixed(6)}, ${mapClickCoord.lng.toFixed(6)}`);
                    setCoordCopied(true);
                    setTimeout(() => setCoordCopied(false), 2000);
                  }}
                >
                  {coordCopied ? '✓ Copied' : 'Copy'}
                </button>
                <button className="coord-close-btn" onClick={() => setMapClickCoord(null)}>✕</button>
              </div>
            )}

            {/* Map Stats Overlay */}
            <div className="map-stats">
              {selectedEntityId && entitySalesPoints.length > 0 && (
                <div className="map-stat" style={{ borderLeftColor: '#C49A3A' }}>
                  <label>Sales Points:</label>
                  <span>{entitySalesPoints.length}</span>
                </div>
              )}
              {selectedRoutes.length > 0 && (
                <>
                  <div className="map-stat">
                    <label>Routes:</label>
                    <span>{selectedRoutes.length}</span>
                  </div>
                  <div className="map-stat">
                    <label>Waypoints:</label>
                    <span>{routeWaypoints.length}</span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        </>
        )}
      </div>
    </div>
  );
}